import { first } from 'lodash-es';
import { conformToMask } from 'react-text-mask-modern';

import { ALL_COUNTRIES, getPhoneMask, getPhonePrefix } from '../Geo';

import type { IPhoneNumber } from './types';

const E164_REGEX =
  /^\+(1|2(?:0|1[12368]|2\d|3\d|4\d|5[0-8]|6\d|7|9[01789])|3(?:[0-4]|5\d|6|7[0-8]|8[0125679]|9)|4(?:0|1|2[013]|[3-9])|5(?:0\d|[1-8]|9\d)|6(?:[0-6]|7[023456789]|8[012356789]|9[012])|7|8(?:0[08]|1|2|4|5[02356]|6|7[08]|8[012368])|9(?:[0-4]|5|6[0-8]|7[012345679]|8|9[234568]))(\d+)(?:;ext=(\d+))?$/;

// Handle E.164-formatted telephone numbers. PhoneNumberFields will
// return their values in this (very constrained) format. We can match it
// with a single regex and save a lot of guesswork.
const getFormattedE164Number = (e164Match: RegExpExecArray): string => {
  const [, country, number, extension] = e164Match;
  let formattedNumber;
  if (country === '1' && number?.length === 10) {
    // Format +12015555555 => (201) 555-5555
    formattedNumber = `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
  } else {
    // Format +441212345678 => +44 1212345678
    formattedNumber = `+${country} ${number}`;
  }

  if (extension) {
    formattedNumber += ` ext. ${extension}`;
  }

  return formattedNumber;
};

/**
 * Converts a telephone number integer value into
 * (xxx) xxx-xxxx or +xx xxxxxxx
 *
 * @param telephoneNumber - The telephone number to format. Accepts string or object types.
 */
export const formatPhoneNumber = (telephoneNumber: string | IPhoneNumber): string => {
  if (!telephoneNumber) {
    return '';
  }

  let number;
  let extension;
  let formattedNumber;

  // Handle telephoneNumber objects, generated by clr-phone-input in some cases
  // when a field is represented with a PhoneNumberField on the back-end.
  // eslint-disable-next-line
  if (isInstanceOfIPhoneNumber(telephoneNumber)) {
    number = telephoneNumber.national_number;
    if (!number) {
      return '';
    }

    const prefix = getPhonePrefix(telephoneNumber.country);
    extension = telephoneNumber.extension;

    formattedNumber = prefix + number;
    if (extension) {
      formattedNumber += `;ext=${extension}`;
    }
  } else {
    formattedNumber = telephoneNumber;
  }

  const e164Match = E164_REGEX.exec(formattedNumber);
  if (e164Match) {
    return getFormattedE164Number(e164Match);
  }

  // Clean +-() characters from the input
  const value = formattedNumber
    .toString()
    .trim()
    // eslint-disable-next-line no-useless-escape
    .replace(/[\+\-()\s]/g, '');

  // If there's anything other than numbers left in the input, return the
  // original value directly.
  if (value.match(/[^0-9]/)) {
    return formattedNumber;
  }

  let city;

  switch (value.length) {
    // Assumes US/Canada +1 telephone numbers
    // To handle more cases up to 15 digits, refer to https://en.wikipedia.org/wiki/List_of_country_calling_codes
    case 10: // PPP#######
      city = value.slice(0, 3);
      number = value.slice(3);
      break;
    case 11: // CPPP#######
      city = value.slice(1, 4);
      number = value.slice(4);
      break;
    default:
      return formattedNumber;
  }

  number = `${number.slice(0, 3)}-${number.slice(3)}`;

  const formattedNumberWithCity = `(${city}) ${number}`.trim();

  if (extension) {
    return `${formattedNumberWithCity} ext. ${extension}`;
  }
  return formattedNumberWithCity;
};

/**
 * FIXME: Short-term replacement for default PhoneNumber constructor as
 * we migrate from models to interfaces. We should review how it's used
 * and determine if this is really needed or if components should be
 * adjusted to handle optionally present phone numbers.
 */
export function getDefaultPhoneNumber(): IPhoneNumber {
  return {
    country: 'US',
    national_number: '',
    extension: '',
  };
}
/**
 * This utility function is needed to check types before serializing `IPhoneNumber`
 * to string.
 */
export function isInstanceOfIPhoneNumber(object: unknown): object is IPhoneNumber {
  return (
    typeof object === 'object' &&
    object !== null &&
    'country' in object &&
    'national_number' in object
  );
}

export function parsePhoneNumber(phoneNumber?: string | null): IPhoneNumber {
  let formattedPhoneNumber = typeof phoneNumber === 'string' ? phoneNumber.trim() : '';
  // default to US country code if none provided
  if (formattedPhoneNumber && !formattedPhoneNumber.startsWith('+')) {
    formattedPhoneNumber = `+1${formattedPhoneNumber}`;
  }

  let phoneCountry = 'US';
  let phonePrefix = '';
  let phoneNatlNumber = '';
  let phoneExtension: string | undefined = '';

  // guess US for +1 country codes - default parser picks first alphabetical match on country code
  const countryIsUS = formattedPhoneNumber.startsWith('+1');
  const matched = E164_REGEX.exec(formattedPhoneNumber);
  if (matched) {
    [, phonePrefix = '', phoneNatlNumber = '', phoneExtension] = matched;

    const matchedCountryPrefix = `+${phonePrefix}`;
    phoneCountry =
      first(
        ALL_COUNTRIES.filter((country: string) => getPhonePrefix(country) === matchedCountryPrefix)
      ) || 'US';
  }
  if (countryIsUS) {
    phoneCountry = 'US';
  }
  return {
    country: phoneCountry,
    national_number: phoneNatlNumber,
    extension: phoneExtension,
  };
}

export function formatAsE164(phoneNumber: IPhoneNumber): string {
  return `${getPhonePrefix(phoneNumber.country)}${phoneNumber.national_number}${
    phoneNumber.extension ? `;ext=${phoneNumber.extension}` : ''
  }`;
}

const MASK_DEFINITIONS = {
  '9': /\d/,
};

/**
 * Converts mask definitions from `ui-mask` style used in python formatting to `text-mask` style
 * used in react-text-mask formatting.
 *
 * For example:
 *
 * `uiMaskToTextMask('(999) 999-9999')` returns
 * `["(", /\d/, /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/]`
 */
function uiMaskToTextMask(uiMask: string): (string | RegExp)[] {
  return Array.from(uiMask).map((c) => MASK_DEFINITIONS[c as keyof typeof MASK_DEFINITIONS] ?? c);
}

/**
 * Format phone number with optional country mask.
 * For example: Great Britain number would be formatted as `+44 02071838750`
 * @param phoneNumber object
 * @returns formatted phone number
 */
export function formatPhoneNumberWithMask(phoneNumber: IPhoneNumber): string {
  const countryPhoneMask = getPhoneMask(phoneNumber.country);
  const mask = countryPhoneMask ? uiMaskToTextMask(countryPhoneMask) : undefined;
  const prefix = getPhonePrefix(phoneNumber.country);
  const maskedContent = mask
    ? conformToMask(phoneNumber.national_number, mask).conformedValue
    : phoneNumber.national_number;
  if (!prefix) {
    return maskedContent;
  }
  return `${prefix} ${maskedContent}`;
}
